(function () {
  if (window.customElements.get('supported-cars-embed')) return;

  if (!window.__scEmbedDefineGuard) {
    window.__scEmbedDefineGuard = true;
    const originalDefine = customElements.define.bind(customElements);
    customElements.define = function (name, constructor, options) {
      if (customElements.get(name)) return;
      try { return originalDefine(name, constructor, options); }
      catch (e) { console.warn('[supported-cars-embed] define', name, e); }
    };
  }

  // Marks the fetched wrapper so CSS can target the embedded copy of the app
  // separately from the real /apps/supported-cars page, whose markup is
  // otherwise identical.
  const EMBED_CLASS = 'app-supported-cars--embed';

  const BASE_PATH = '/apps/supported-cars';

  // One Intelligems custom event per funnel step. Each name must be registered by
  // hand in the Intelligems Events Manager (Custom Event -> "Event Identifier (in
  // theme code)") before it can back a metric — these strings are that contract.
  // Hardcoded rather than built from a section setting: text settings are rewritten
  // by Translate & Adapt, the same reason the section's anchor id is hardcoded.
  // Keyed by the `type` the app reports for make and model. Generation is keyed to
  // match, but is fired from onClick instead — see the note there.
  const IG_EVENTS = {
    make: 'sc_embed_brand_selected',
    model: 'sc_embed_model_selected',
    generation: 'sc_embed_generation_selected',
  };

  class SupportedCarsEmbed extends HTMLElement {
    connectedCallback() {
      if (this._loaded) return;
      this._loaded = true;
      this._prefetchCache = new Map();
      this._trackEvents = this.dataset.igEvents === 'true';
      this._sentEvents = new Set();
      const root = (this.dataset.localeRoot || '').replace(/\/$/, '');
      this._localeRoot = root && root !== '/' ? root : '';
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('mouseover', this.onHover.bind(this));
      this.addEventListener('touchstart', this.onHover.bind(this), { passive: true });
      this.installSelectionInterceptor();
      this.load(this.dataset.src || '/apps/supported-cars');
    }

    localize(href) {
      if (!href || typeof href !== 'string') return href;
      if (!this._localeRoot) return href;
      if (!href.startsWith('/apps/supported-cars')) return href;
      return this._localeRoot + href;
    }

    isSupportedCarsHref(href) {
      if (!href || typeof href !== 'string') return false;
      if (href.startsWith('/apps/supported-cars')) return true;
      if (this._localeRoot && href.startsWith(this._localeRoot + '/apps/supported-cars')) return true;
      return false;
    }

    installSelectionInterceptor() {
      const handler = (e) => {
        if (!e || !e.detail) return;
        const { href, type } = e.detail;
        if (!href || typeof href !== 'string') return;
        if (!href.includes(BASE_PATH)) return;
        // Tracked from inside this handler on purpose: the stopImmediatePropagation
        // below silences every window listener registered after this one, so a
        // separate `supported-cars-selection` tracking listener would never fire.
        //
        // Guarded because of where it sits: anything thrown here would skip the
        // stopImmediatePropagation below, handing the click back to the app's own
        // handler — which navigates away instead of loading the step inline. A
        // reporting concern must never be able to break the embed itself.
        try {
          this.track(IG_EVENTS[type]);
        } catch (err) {
          console.warn('[supported-cars-embed] track', err);
        }
        e.stopImmediatePropagation();
        let path = href;
        try { path = new URL(href, window.location.origin).pathname + new URL(href, window.location.origin).search; } catch (_) {}
        this.load(this.localize(path));
      };
      window.addEventListener('supported-cars-selection', handler, true);
      window.addEventListener('supported-cars-selection', handler);
    }

    onHover(e) {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      const rawHref = a.getAttribute('href') || '';
      if (!this.isSupportedCarsHref(rawHref)) return;
      const href = this.localize(rawHref);
      if (this._prefetchCache.has(href)) return;
      const p = fetch(href, { credentials: 'same-origin' }).then(r => r.ok ? r.text() : null).catch(() => null);
      this._prefetchCache.set(href, p);
    }

    onClick(e) {
      const a = e.target.closest('a[href]');
      if (!a) return;

      // The app wraps the make and model option lists in a <supported-cars-selector>
      // element that intercepts clicks and dispatches `supported-cars-selection`, but
      // builds the generation list as a plain <div> (updateGenerationSelector in the
      // app's supported-cars-selection.js). Nothing announces a generation pick, so
      // this listener is the only place the third step is observable.Only observed, never intercepted: the navigation to the
      // app's result page is left alone, and Intelligems sends over sendBeacon, which
      // is built to survive the unload that follows.
      if (a.closest('.supported-cars-selector--generation')) {
        try {
          this.track(IG_EVENTS.generation);
        } catch (err) {
          console.warn('[supported-cars-embed] track', err);
        }
      }

      const rawHref = a.getAttribute('href') || '';
      if (!this.isSupportedCarsHref(rawHref)) return;
      if (a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      this.load(this.localize(rawHref));
    }

    async load(src) {
      if (this._inFlight) this._inFlight.abort();
      const ctrl = new AbortController();
      this._inFlight = ctrl;

      this.classList.add('is-loading');
      try {
        let html;
        const cached = this._prefetchCache && this._prefetchCache.get(src);
        if (cached) {
          html = await cached;
          this._prefetchCache.delete(src);
        }
        if (!html) {
          const res = await fetch(src, { credentials: 'same-origin', signal: ctrl.signal });
          if (!res.ok) throw new Error('Failed to fetch supported cars page: ' + res.status);
          html = await res.text();
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const app = doc.getElementById('app-supported-cars');
        if (!app) {
          this.renderError();
          return;
        }

        this.injectStyles(doc);
        this.injectJsonScripts(doc);

        const stripSelectors = [
          'supported-cars-breadcrumbs',
          '.app-sc-breadcrumbs',
          '.supported-cars-selection-header',
          '.sc-upsell-container',
          // Coupled to the default-layout-only gate in carista-app/frontend/client/styles/main.css.
          // carista-app is mid-migration from data-ab="b" to data-variant="desktop" (see its
          // docs/superpowers/specs/2026-07-31-ab-to-default-desktop-rename-design.md) — matching
          // both here bridges the gap regardless of deploy order. Once that rename is deployed
          // and confirmed live, drop the '[data-ab="b"]' branch below.
          '[data-ab="b"], [data-variant="desktop"]',
          // The A/B tool's bucket stylesheet is global to THIS page, so it reaches inside
          // the embed too. Drop the challenger copies here; the surviving controls are
          // unmarked below.
          '[data-experiment="b"]',
        ];
        stripSelectors.forEach(sel => {
          app.querySelectorAll(sel).forEach(el => el.remove());
        });

        // Must run AFTER the strip above, which needs the attribute to find the copies.
        // The group-B sheet also carries [data-experiment="a"]{display:none!important}, so
        // leaving the attribute on would blank the embed for every bucketed visitor —
        // stripping the b copies without this is worse than doing neither.
        app.querySelectorAll('[data-experiment]').forEach(el => el.removeAttribute('data-experiment'));

        this.injectComponentScript(doc);

        app.classList.add(EMBED_CLASS);

        this.innerHTML = app.outerHTML;
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[supported-cars-embed]', err);
        this.renderError();
      } finally {
        if (this._inFlight === ctrl) this._inFlight = null;
        this.classList.remove('is-loading');
      }
    }

    injectStyles(doc) {
      this._injectedStyleHashes = this._injectedStyleHashes || new Set();
      const styles = Array.from(doc.querySelectorAll('style')).filter(s =>
        /sc-|supported-cars|--sc-|result-content|result-sticky|supported-cars-result|supported-cars-feature/.test(s.textContent)
      );
      styles.forEach(styleEl => {
        const text = styleEl.textContent;
        const hash = text.length + ':' + text.slice(0, 64);
        if (this._injectedStyleHashes.has(hash)) return;
        this._injectedStyleHashes.add(hash);
        const clone = styleEl.cloneNode(true);
        clone.dataset.supportedCarsEmbed = '';
        document.head.appendChild(clone);
      });
    }

    injectJsonScripts(doc) {
      ['supportedCarsConfig', 'supportedCarsIcons'].forEach(id => {
        if (document.getElementById(id)) return;
        const src = doc.getElementById(id);
        if (!src) return;
        const s = document.createElement('script');
        s.type = 'application/json';
        s.id = id;
        s.textContent = src.textContent;
        document.body.appendChild(s);
      });
    }

    injectComponentScript(doc) {
      this._injectedScriptHashes = this._injectedScriptHashes || new Set();
      const scripts = Array.from(doc.querySelectorAll('script:not([src])')).filter(s => {
        const type = s.getAttribute('type');
        if (type && type !== 'text/javascript' && type !== 'module') return false;
        return /customElements\.define\(["'](?:supported-cars|sc-|search-bar|collapsible-)/.test(s.textContent);
      });

      if (!scripts.length) return;

      const origAdd = window.addEventListener;
      const patchedAdd = function (type, listener) {
        if (type === 'DOMContentLoaded') {
          try { Promise.resolve().then(() => listener.call(window, new Event('DOMContentLoaded'))); } catch (e) { console.warn('[supported-cars-embed] DCL', e); }
          return;
        }
        return origAdd.apply(this, arguments);
      };

      scripts.forEach(scriptEl => {
        const text = scriptEl.textContent;
        const hash = text.length + ':' + text.slice(0, 80);
        if (this._injectedScriptHashes.has(hash)) return;
        this._injectedScriptHashes.add(hash);

        window.addEventListener = patchedAdd;
        try {
          const s = document.createElement('script');
          if (scriptEl.type === 'module') s.type = 'module';
          s.textContent = text;
          document.body.appendChild(s);
        } finally {
          window.addEventListener = origAdd;
        }
      });
    }

    // Once per event name per page view, and deliberately not persisted beyond it:
    // this matches how the app reports its own Intelligems events (a module-level flag
    // in its supported-cars-result.js, reset on every page load), so these numbers sit
    // on the same basis as the previous app-page test rather than a stricter one.
    //
    // It bounds the obvious noise — reopening a dropdown and rechoosing counts once —
    // without claiming to be a headcount. Intelligems reports conversion as the share
    // of visitors who fired an event at least once, so the per-visitor figures are
    // unaffected either way; only raw event volume is.
    track(name) {
      if (!this._trackEvents || !name) return;

      this._sentEvents = this._sentEvents || new Set();
      if (this._sentEvents.has(name)) return;
      this._sentEvents.add(name);

      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: name });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name });
    }

    renderError() {
      this.innerHTML = '<div class="supported-cars-embed__error">Unable to load the supported cars list. <a href="/apps/supported-cars">Open the full page</a>.</div>';
    }
  }

  customElements.define('supported-cars-embed', SupportedCarsEmbed);
})();
